import React, { FunctionComponent, useRef, useState, useEffect } from 'react';
import AppButton from 'components/AppButton/AppButton';
import { CSVLink } from 'react-csv';
import { DownloadIcon, toaster } from 'evergreen-ui';
import { MotiviewDetailsList } from '../../../typings/target_group';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
    useLazyGetMotiviewDetailsQuery,
    useLazyGetParticipantsQuery,
} from 'store/api/participants';
import { retryWithBackoff } from 'utils/retry';

export interface TargetGroupMotiviewDownloadProps {
    companyId: string;
    companyName: string;
    groupId: string;
    assessmentName: string | undefined;
    assessmentLanguage: string | undefined;
    targetGroupName: string;
    totalParticipants: number;
}

const TargetGroupMotiviewDownload: FunctionComponent<
    TargetGroupMotiviewDownloadProps
> = (props) => {
    const {
        companyId,
        companyName,
        groupId,
        assessmentName,
        assessmentLanguage,
        targetGroupName,
        totalParticipants,
    } = props;

    const [getParticipants] = useLazyGetParticipantsQuery();
    const [getMotiviewDetails] = useLazyGetMotiviewDetailsQuery();

    const csvLink = useRef<
        CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
    >(null);
    const isRunningRef = useRef<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
    const [isDone, setIsDone] = useState(false);
    const [processedCount, setProcessedCount] = useState<number>(0);
    const [resumeToken, setResumeToken] = useState<string | null>(null);
    const [resumeDetails, setResumeDetails] = useState<MotiviewDetailsList>({});

    const [csvData, setCsvData] = useState<
        {
            [p: string]: string | number | undefined;
        }[]
    >([]);

    const isDutch = assessmentLanguage === 'nl-NL';
    const headers = {
        scanName: isDutch ? 'Scan naam' : 'Scan name',
        groupName: isDutch ? 'Groep naam' : 'Group name',
        candidateId: 'Candidate ID',
        fullName: isDutch ? 'Volledige naam' : 'Full name',
        email: 'Email',
        phone: isDutch ? 'Telefoonnummer' : 'Phone',
        status: 'Status',
        engagementScore: isDutch ? 'Betrokkenheid score' : 'Engagement score',
        registeredAt: isDutch ? 'Geregistreerd op' : 'Registered at',
    };

    // Format an ISO timestamp as `YYYY-MM-DD HH:MM` in the viewer's local
    // timezone. Spreadsheets parse this as a datetime, it sorts as a
    // string, and it's locale-neutral (no DD/MM vs MM/DD ambiguity).
    // Falls back to '-' for missing or unparseable values so the CSV
    // column never contains a raw ISO string or "Invalid Date".
    const formatRegisteredAt = (iso: string | undefined | null): string => {
        if (!iso) return '-';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '-';
        const pad = (n: number) => String(n).padStart(2, '0');
        return (
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
            `${pad(d.getHours())}:${pad(d.getMinutes())}`
        );
    };

    // Map a numeric engagement score to its label, matching the thresholds the
    // motiview backend uses in MotiviewService.getParticipantStatus:
    //   < 40              → Below average
    //   >= 40 && < 60     → Average
    //   >= 60             → Above average
    // 'NO_SCORE', missing, or 0 falls back to '-' since the backend treats 0
    // as "no score produced" (analysis-prediction clamps real scores to 15-100).
    const engagementScoreLabel = (
        score: number | 'NO_SCORE' | undefined | null,
    ): string => {
        if (
            score === undefined ||
            score === null ||
            score === 'NO_SCORE' ||
            score === 0
        ) {
            return '-';
        }
        if (score < 40) {
            return isDutch ? 'Onder gemiddeld' : 'Below average';
        }
        if (score < 60) {
            return isDutch ? 'Gemiddeld' : 'Average';
        }
        return isDutch ? 'Boven gemiddeld' : 'Above average';
    };

    function wait(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const runDownload = async (
        startToken: string,
        startDetails: MotiviewDetailsList,
        isResume: boolean,
    ) => {
        if (isRunningRef.current) return;
        isRunningRef.current = true;

        try {
            const limit = 80;
            const allDetails: MotiviewDetailsList = { ...startDetails };

            setIsSaving(true);
            setIsPaused(false);
            setIsDone(false);
            setCsvData([]);
            if (!isResume) setProcessedCount(0);

            let nextToken = startToken;
            let lastAttemptedToken = startToken;

            try {
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const tokenForThisPage = nextToken;
                    lastAttemptedToken = tokenForThisPage;
                    const { participants, detailsList, nextNextToken } =
                        await retryWithBackoff(async () => {
                            const participantsPage = await getParticipants({
                                companyIdentifier: companyId,
                                groupId,
                                limit,
                                includeResults: false,
                                includeTotal: false,
                                nextToken: tokenForThisPage,
                            }).unwrap();

                            const detailsForPage = await getMotiviewDetails({
                                companyIdentifier: companyId,
                                groupId,
                                userIds: participantsPage.participants.map(
                                    (p) => p.user_id,
                                ),
                            }).unwrap();

                            return {
                                participants: participantsPage.participants,
                                detailsList: detailsForPage,
                                nextNextToken: participantsPage.nextToken,
                            };
                        });

                    Object.assign(allDetails, detailsList);
                    setProcessedCount((prev) => prev + participants.length);

                    const next = nextNextToken ?? '';
                    if (!next) break;
                    nextToken = next;
                    await wait(1000);
                }
            } catch (e) {
                console.error('Error getting motiview details', e);
                setResumeToken(lastAttemptedToken);
                setResumeDetails(allDetails);
                setIsSaving(false);
                setIsPaused(true);
                toaster.warning(
                    'Download paused. Click "Continue download" to resume.',
                );
                return;
            }

            setResumeToken(null);
            setResumeDetails({});

            if (Object.keys(allDetails).length) {
                setCsvData(
                    Object.keys(allDetails).map((userId) => {
                        const detail = allDetails[userId];
                        const questions = detail.questions || [];
                        const getValue = (id: string) =>
                            questions.find((q) => q.id === id)?.value || '-';

                        return {
                            [headers.scanName]: assessmentName,
                            [headers.groupName]: targetGroupName,
                            [headers.candidateId]: userId,
                            [headers.fullName]: getValue('full_name'),
                            [headers.email]: getValue('email'),
                            [headers.phone]: getValue('phone'),
                            [headers.status]: getValue('status'),
                            [headers.engagementScore]: engagementScoreLabel(
                                detail.engagement_score,
                            ),
                            [headers.registeredAt]: formatRegisteredAt(
                                detail.submitted_at,
                            ),
                        };
                    }),
                );
            }

            if (!Object.keys(allDetails).length) {
                setIsButtonDisabled(true);
            }
            setIsDone(true);
        } finally {
            isRunningRef.current = false;
        }
    };

    const downloadDetails = () => {
        void runDownload('', {}, false);
    };
    const continueDownload = () => {
        if (resumeToken !== null) {
            void runDownload(resumeToken, resumeDetails, true);
        }
    };

    useEffect(() => {
        if (csvData.length && isSaving) {
            csvLink?.current?.link.click();
            setIsSaving(false);
            setProcessedCount(0);
        }
    }, [csvData, isSaving]);

    const progress = totalParticipants
        ? Math.min((processedCount / totalParticipants) * 100, 100)
        : 0;

    return (
        <div aria-label="download candidate details button">
            {isSaving && !isDone && !isPaused && (
                <div>
                    <LinearProgress variant="determinate" value={progress} />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                        Downloading candidate details...
                    </div>
                </div>
            )}
            {isPaused && (
                <div>
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                        Download paused at {processedCount}
                        {totalParticipants
                            ? ` / ${totalParticipants}`
                            : ''}{' '}
                        candidates.
                    </div>
                    <AppButton
                        justifyContent={'flex-start'}
                        width={'auto'}
                        icon={DownloadIcon}
                        isDisabled={false}
                        onClick={continueDownload}>
                        Continue download
                    </AppButton>
                </div>
            )}
            {!isPaused && (isDone || !isSaving) && (
                <AppButton
                    justifyContent={'flex-start'}
                    width={'auto'}
                    icon={DownloadIcon}
                    isDisabled={isSaving || isButtonDisabled}
                    onClick={() => downloadDetails()}>
                    Download candidate details
                </AppButton>
            )}
            <CSVLink
                data={csvData}
                filename={
                    companyName +
                    '-' +
                    targetGroupName +
                    '-candidate-details.csv'
                }
                className="hidden"
                ref={csvLink}
                target={'_blank'}
            />
        </div>
    );
};

export default TargetGroupMotiviewDownload;
